import {
  Box,
  Button,
  Heading,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useNavigate } from "react-router-dom"
import SectionHeading from "../components/common/SectionHeading"
import StatCard from "../components/common/StatCard"
import { useActivePortfolio } from "../hooks/useActivePortfolio"
import { useUserDetails } from "../hooks/useAuthQueries"
import { usePortfolioStats } from "../hooks/usePortfolioQueries"
import PilotAnalyticsPanel from "../pilot/components/PilotAnalyticsPanel"
import { formatCo2Kg } from "../utils/carbonUtils"

const fmt = {
  currency: (val: number) =>
    `£${val.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`,
  roi: (val: number | null | undefined) =>
    val != null ? `${val.toFixed(1)}%` : "–",
}

const SkeletonCard: React.FC<{ hasSubline?: boolean }> = ({ hasSubline }) => (
  <Box bg="gray.100" py={4} px={6} borderRadius="lg">
    <Skeleton h="10px" w="55%" mb={3} borderRadius="sm" />
    <Skeleton h="24px" w="38%" mb={hasSubline ? 2 : 0} borderRadius="sm" />
    {hasSubline && <Skeleton h="8px" w="62%" borderRadius="sm" />}
  </Box>
)

const DashboardSkeleton: React.FC = () => (
  <VStack gap={8} align="stretch">
    <Box>
      <Skeleton h="13px" w="68px" borderRadius="sm" mb={4} />
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} mb={4}>
        {[0, 1, 2, 3].map((i) => <SkeletonCard key={i} />)}
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={4}>
        {[0, 1, 2].map((i) => <SkeletonCard key={i} hasSubline />)}
      </SimpleGrid>
    </Box>
    <Box>
      <Skeleton h="13px" w="136px" borderRadius="sm" mb={4} />
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
        {[0, 1, 2, 3].map((i) => <SkeletonCard key={i} hasSubline />)}
      </SimpleGrid>
    </Box>
  </VStack>
)

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const { data: userDetails } = useUserDetails()
  const { activePortfolioName } = useActivePortfolio()
  const org = userDetails?.data?.organisation
  const activePortfolioId = userDetails?.data?.activePortfolioId

  const {
    data: portfolioStats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = usePortfolioStats(activePortfolioId)

  const isLoading = statsLoading
  const hasError = !!statsError

  const analysedHomes = (portfolioStats?.totalHomes ?? 0) - (portfolioStats?.unanalysedHomes ?? 0)
  const roiPotentialCounts = portfolioStats?.roiPotentialCounts
  const highCount = roiPotentialCounts?.high ?? 0
  const mediumCount = roiPotentialCounts?.medium ?? 0
  const lowCount = roiPotentialCounts?.low ?? 0

  const totalDeployment = portfolioStats?.totalDeploymentCost ?? 0
  const totalSavings = portfolioStats?.totalAnnualSavings ?? 0
  const highestRoi = portfolioStats?.highestRoi ?? null
  const avgRoi = portfolioStats?.avgRoi ?? null

  const totalSolarMwh = portfolioStats?.totalSolarKwh != null ? portfolioStats.totalSolarKwh / 1000 : null
  const totalSolarExportedMwh = portfolioStats?.totalSolarExportedKwh != null ? portfolioStats.totalSolarExportedKwh / 1000 : null
  const totalCo2SavingsKg = portfolioStats?.totalCo2SavingsKg ?? null
  const totalEmbodiedKgCo2e = portfolioStats?.totalEmbodiedKgCo2e ?? null
  const carbonPaybackYears = portfolioStats?.carbonPaybackYears ?? null

  return (
    <Box p={{ base: 4, md: 6, lg: 8 }}>
      <VStack gap={8} align="stretch">
        <Box>
          <Heading size="xl">Dashboard</Heading>
          {activePortfolioName && (
            <Text fontSize="sm" color="gray.600" mt={1}>{activePortfolioName}</Text>
          )}
        </Box>

        {isLoading ? (
          <DashboardSkeleton />
        ) : hasError ? (
          <Box
            bg="white"
            p={8}
            borderRadius="lg"
            border="1px solid"
            borderColor="red.100"
            textAlign="center"
          >
            <VStack gap={3}>
              <Text color="red.600" fontWeight="medium">
                Could not load dashboard data.
              </Text>
              <Text fontSize="sm" color="gray.500">
                Check your connection and try again.
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { refetchStats() }}
              >
                Retry
              </Button>
            </VStack>
          </Box>
        ) : (
          <VStack gap={8} align="stretch">
            <VStack align="stretch" gap={0}>
              <SectionHeading>My Homes</SectionHeading>
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} mb={4}>
                <StatCard label="Total homes analysed" value={analysedHomes} />
                <StatCard
                  label="High ROI Potential"
                  value={highCount}
                  subline="Green ROI of 8% or more"
                  onClick={() => navigate("/my-homes?roiPotential=high")}
                />
                <StatCard
                  label="Medium ROI Potential"
                  value={mediumCount}
                  subline="Green ROI from 4% to under 8%"
                  onClick={() => navigate("/my-homes?roiPotential=medium")}
                />
                <StatCard
                  label="Low ROI Potential"
                  value={lowCount}
                  subline="Green ROI below 4%"
                  onClick={() => navigate("/my-homes?roiPotential=low")}
                />
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} gap={4}>
                <StatCard
                  label="Total Retrofit Deployment"
                  value={fmt.currency(totalDeployment)}
                  subline="across all homes in this portfolio"
                />
                <StatCard
                  label="Total Energy Savings"
                  value={fmt.currency(totalSavings)}
                  subline="annual, across all homes"
                />
                <StatCard
                  label="Highest ROI"
                  value={fmt.roi(highestRoi)}
                  subline={`Average ROI: ${fmt.roi(avgRoi)}`}
                />
              </SimpleGrid>
            </VStack>

            <VStack align="stretch" gap={0}>
              <SectionHeading>Environmental Impact</SectionHeading>
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
                <StatCard
                  label="Solar generation (all homes)"
                  value={totalSolarMwh != null ? `${totalSolarMwh.toLocaleString("en-GB", { maximumFractionDigits: 1 })} MWh` : "–"}
                  subline="Total recommended PV output per year"
                />
                <StatCard
                  label="Solar exported (all homes)"
                  value={totalSolarExportedMwh != null ? `${totalSolarExportedMwh.toLocaleString("en-GB", { maximumFractionDigits: 1 })} MWh` : "–"}
                  subline="Generation minus household electricity demand"
                />
                <StatCard
                  label="Annual operational CO₂ saved"
                  value={totalCo2SavingsKg != null ? formatCo2Kg(totalCo2SavingsKg) : "–"}
                  subline="Pre- vs post-upgrade, across analysed homes"
                />
                <StatCard
                  label="Total embodied carbon"
                  value={
                    totalEmbodiedKgCo2e != null
                      ? formatCo2Kg(totalEmbodiedKgCo2e)
                      : "–"
                  }
                  subline={
                    carbonPaybackYears != null
                      ? `Offset in ~${carbonPaybackYears.toLocaleString("en-GB", { maximumFractionDigits: 1 })} yrs by operational savings`
                      : "Offset period unavailable"
                  }
                />
              </SimpleGrid>
            </VStack>
          </VStack>
        )}

        {org?.isPilot && org?.councilSlug && (
          <PilotAnalyticsPanel councilSlug={org.councilSlug} />
        )}
      </VStack>
    </Box>
  )
}

export default Dashboard
